import type React from 'react';
import type { ReactNode, RefObject } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { hasActiveOverlays } from '../FullscreenOverlay';

type PanelContent = React.ComponentType<{ config?: unknown }>;
type PanelProvider = React.ComponentType<{ children: ReactNode }>;

type OpenPanelOptions = {
  provider?: PanelProvider;
  config?: unknown;
};

type DetailPanelContextValue = {
  activePanelId: string | null;
  panelConfig: unknown;
  activeContent: PanelContent | null;
  activeProvider: PanelProvider | null;
  openPanel: (panelId: string, content: PanelContent, options?: OpenPanelOptions) => void;
  closePanel: () => void;
  isMobile: boolean;
};

const DetailPanelContext = createContext<DetailPanelContextValue | null>(null);

type DetailPanelProviderProps = {
  children: ReactNode;
  isMobile: boolean;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
};

export const DetailPanelProvider = ({ children, isMobile, scrollContainerRef }: DetailPanelProviderProps) => {
  const [activePanelId, setActivePanelId] = useState<string | null>(null);
  const [panelConfig, setPanelConfig] = useState<unknown>(undefined);
  const [activeContent, setActiveContent] = useState<PanelContent | null>(null);
  const [activeProvider, setActiveProvider] = useState<PanelProvider | null>(null);
  const savedScrollPositionRef = useRef(0);

  const openPanel = useCallback(
    (panelId: string, content: PanelContent, options?: OpenPanelOptions) => {
      setPanelConfig(options?.config);
      setActiveContent(() => content);
      setActiveProvider(() => options?.provider ?? null);
      setActivePanelId(panelId);

      if (!isMobile && scrollContainerRef.current) {
        savedScrollPositionRef.current = scrollContainerRef.current.scrollTop;
      }
    },
    [isMobile, scrollContainerRef],
  );

  const closePanel = useCallback(() => {
    setActivePanelId(null);
    setPanelConfig(undefined);
    setActiveContent(null);
    setActiveProvider(null);

    if (!isMobile && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = savedScrollPositionRef.current;
    }
  }, [isMobile, scrollContainerRef]);

  useEffect(() => {
    if (isMobile || !activePanelId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !hasActiveOverlays()) {
        closePanel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, activePanelId, closePanel]);

  return (
    <DetailPanelContext.Provider
      value={{ activePanelId, panelConfig, activeContent, activeProvider, openPanel, closePanel, isMobile }}
    >
      {children}
    </DetailPanelContext.Provider>
  );
};

export const useDetailPanel = () => {
  const context = useContext(DetailPanelContext);
  if (!context) {
    throw new Error('useDetailPanel must be used within a DetailPanelProvider');
  }
  return context;
};

export const useDetailPanelSafe = () => useContext(DetailPanelContext);
